import { cn } from "@/lib/utils";
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

export interface EtfBreadcrumbProps {
  items: BreadcrumbItem[];
  className?: string;
  separator?: ReactNode;
}

/**
 * Shared breadcrumb for all ETF website pages.
 *
 * - Items with `href` render as `<Link>`.
 * - The last item (no `href`) renders as the current-page label.
 * - `className` allows overriding wrapper styles.
 * - `separator` controls the divider between items (default `<span>/</span>`).
 */
export function EtfBreadcrumb({
  items,
  className,
  separator = <span>/</span>,
}: EtfBreadcrumbProps) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 px-10 py-3.5 border-b border-[var(--etf-border2)] font-[family-name:var(--font-instrument)] text-[14px] tracking-[0.06em] text-[var(--etf-white3)]",
        // ≤768 canonical (fund-page.css:1703-1708): padding 14px 20px, font 12px,
        // gap STAYS 8px (canonical does NOT touch gap until ≤480).
        "max-md:px-5 max-md:py-[14px] max-md:text-[12px]",
        // ≤480 canonical (fund-page.css:1859-1863): padding 12px 16px, font 11px, gap 6px
        "max-[480px]:px-4 max-[480px]:py-3 max-[480px]:text-[11px] max-[480px]:gap-1.5",
        "[&_a]:text-[var(--etf-white3)] [&_a]:no-underline [&_a]:transition-colors [&_a]:duration-150 [&_a:hover]:text-[var(--etf-white2)]",
        className,
      )}
    >
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <span key={`${item.label}-${i}`} className="contents">
            {i > 0 && separator}
            {item.href ? (
              <Link to={item.href}>{item.label}</Link>
            ) : isLast ? (
              <span className="text-[var(--etf-white2)] overflow-hidden text-ellipsis whitespace-nowrap max-w-[400px]">
                {item.label}
              </span>
            ) : (
              <span className="transition-colors duration-150 cursor-default hover:text-[var(--etf-white2)]">
                {item.label}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}
